<template>
    <div class="slider-home-wrapper">
        <div class="slider-home" ref="slider">
            <div class="item" v-for="(item, index) in items" :key="index"
                :class="{ 'item-enter': nextIndex === index && isAnimating, 'item-leave': currentIndex === index && isAnimating, 'item-current': currentIndex === index }">
                <div class="background">
                    <div v-img-responsive="{
                        device: device,
                        images: {
                            mobile: item.images.mobile,
                            tablet: item.images.tablet,
                            desktop: item.images.desktop
                        }
                    }" class="inner bg-image-to-load"></div>
                </div>

                <div class="content">
                    <div class="anim-hotel-title" :class="{ 'shown': currentIndex === index }">
                        <p class="hotel-title" :style="{ 'background-color': item.color_code }">
                            {{ item.title }}
                            <span class="icon-star" v-for="(n, index) in item.stars" :key="index"></span>
                        </p>
                    </div>
                    <div class="anim-hotel-room" :class="{ 'shown': currentIndex === index }">
                        <p class="hotel-room" :style="{ 'text-shadow': '3px -3px ' + item.color_code }" v-html="$filters.translate('vue_chambre') + ' <br />' + item.room"></p>
                    </div>
                </div>

                <div class="big-number" :class="{ 'shown': currentIndex === index }"><span
                        v-for="(number, index) in ('' + item.room).split('')" v-text="number" :key="index"></span></div>
            </div>

            <div class="slider-home-nav-wrapper" v-show="moreOneItem">
                <div class="nav-inner">
                    <span class="dot" v-for="(item, index) in items" :key="index"
                        :class="{ 'completed': nextIndex >= index }" @click="setCurrent(index)"></span>

                    <div class="progress-wrapper">
                        <div class="progress-inner">
                            <div class="bar progress-bar"></div>
                            <div class="bar"></div>
                        </div>
                    </div>
                </div>

                <div class="btn-nav">
                    <span class="arrow arrow-gray" @click="previous"></span>
                    <span class="arrow arrow-right arrow-gray" @click="next"></span>
                </div>

                <svg class="form-bottom">
                    <polygon x="0" y="0" points="0,0 200,0 200,200 " />
                </svg>
            </div>
        </div>

        <div class="points-info-wrapper">
            <div class="point-info" v-for="(pointInfo, index) in listPointsInfo" :key="index">
                <div class="point-info-inner">
                    <p class="title" v-text="pointInfo.title"></p>
                    <p class="texte" v-html="$filters.nl2br(pointInfo.text)"></p>

                    <button class="btn-close" @click="hidePointInfo"><span class="icon-cross"></span></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { usePointerSwipe } from '@vueuse/core';
import { useWheel } from '@vueuse/gesture';
import { gsap } from 'gsap';
import vImgResponsive from '@/directives/v-img-responsive';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
    items: {
        type: Array,
        required: true,
        default: () => []
    }
});

const store = useStore();

const slider = ref(null);
const currentIndex = ref(0);
const nextIndex = ref(0);
const autoplay = ref(true);
const autoplayDuration = ref(5);
const isAnimating = ref(false);
const timerAutoplay = ref(null);
const tlSlideTransition = ref(null);
const twlProgressBar = ref(null);

const pageLoaded = computed(() => {
    return store.state.pageLoaded;
});

const device = computed(() => {
    return store.state.device;
});

const beforeIndex = computed(() => {
    return currentIndex.value === 0 ? props.items.length - 1 : currentIndex.value - 1;
});

const afterIndex = computed(() => {
    return currentIndex.value === props.items.length - 1 ? 0 : currentIndex.value + 1;
});

const moreOneItem = computed(() => {
    return props.items.length > 1;
});

const listPointsInfo = computed(() => {
    return props.items.filter(item => item.point_info).map(item => item.point_info);
});

const wheelHandler = ({ movement: [x, y], wheeling }) => {
    if (moreOneItem.value) {
        if (!wheeling) {
            if (y > 0) {
                next();
            } else {
                previous();
            }
        }
    }
};

const previous = () => {
    runTransition(beforeIndex.value, -1);
};

const next = () => {
    runTransition(afterIndex.value, 1);
};

const setCurrent = (index) => {
    if (index !== currentIndex.value) {
        let direction = index > currentIndex.value ? 1 : -1;
        runTransition(index, direction);
    }
};

const runProgress = () => {
    if (autoplay.value && moreOneItem.value) {
        let percent = 100 / props.items.length * (currentIndex.value + 1) + '%';
        if (twlProgressBar.value) {
            twlProgressBar.value.kill();
        }
        twlProgressBar.value = gsap.to('.progress-wrapper .progress-bar', { duration: autoplayDuration.value, width: percent, height: percent, ease: 'linear.none', onComplete: () => { next(); } });
    }
};

const runTransition = (newIndex, direction) => {
    if (!isAnimating.value) {
        nextIndex.value = newIndex;
        isAnimating.value = true;
        let labelLeaveAnim = 'leave-anim-label';
        let labelEnterAnim = 'enter-anim-label';

        setProgress(nextIndex.value);

        clearTimeout(timerAutoplay.value);
        timerAutoplay.value = setTimeout(() => {
            const pointInfoLeave = document.querySelector('.points-info-wrapper .point-info:nth-child(' + (currentIndex.value + 1) + ')');
            const pointInfoEnter = document.querySelector('.points-info-wrapper .point-info:nth-child(' + (nextIndex.value + 1) + ')');
            const pointInfoInnerEnter = pointInfoEnter?.querySelector('.point-info-inner') ?? null;

            const itemEnter = document.querySelector('.item-enter');
            const bigNumberEnter = itemEnter?.querySelectorAll('.big-number span') ?? null;
            const backgroundEnter = itemEnter?.querySelector('.background') ?? null;
            const backgroundInnerEnter = backgroundEnter?.querySelector('.inner') ?? null;
            const hotelTitleEnter = itemEnter?.querySelector('.anim-hotel-title') ?? null;
            const hotelRoomEnter = itemEnter?.querySelector('.anim-hotel-room') ?? null;

            const itemLeave = document.querySelector('.item-leave');
            const bigNumberLeave = itemLeave?.querySelectorAll('.big-number span') ?? null;
            const backgroundLeave = itemLeave?.querySelector('.background') ?? null;
            const backgroundInnerLeave = backgroundLeave?.querySelector('.inner') ?? null;
            const hotelTitleLeave = itemLeave?.querySelector('.anim-hotel-title') ?? null;
            const hotelRoomLeave = itemLeave?.querySelector('.anim-hotel-room') ?? null;
            
            let tl = gsap.timeline({
                onComplete: () => {
                    isAnimating.value = false;
                    currentIndex.value = nextIndex.value;
                    runProgress();
                }
            })
                .set(bigNumberEnter, { y: (100 * direction) + '%' })
                .set(backgroundEnter, { y: '0%' })
                .set(backgroundInnerEnter, { y: (10 * direction) + '%' });

            if (pointInfoEnter) {
                tl.set(pointInfoEnter, { height: '0%' });
            }

            tl.addLabel(labelLeaveAnim);

            if (pointInfoLeave) {
                tl.to(pointInfoLeave, { duration: 0.4, height: 0, ease: 'power2.inOut' }, labelLeaveAnim);
            }

            tl.to(backgroundLeave, { duration: 1.3, y: -(100 * direction) + '%', ease: 'power4.inOut' }, labelLeaveAnim)
                .to(backgroundInnerLeave, { duration: 1.3, y: (90 * direction) + '%', ease: 'power4.inOut' }, labelLeaveAnim)
                .to(backgroundInnerEnter, { duration: 1.3, y: '0%', ease: 'power4.inOut' }, labelLeaveAnim)
                .to(bigNumberLeave, { duration: 1.3, stagger: 0.1, y: -(100 * direction) + '%', ease: 'power4.inOut' }, labelLeaveAnim)
                .to(hotelTitleLeave, { duration: 0.6, className: 'anim-hotel-title' }, labelLeaveAnim)
                .to(hotelRoomLeave, { duration: 0.2, className: 'anim-hotel-room' }, labelLeaveAnim)
                .addLabel(labelEnterAnim)
                .to(bigNumberEnter, { duration: 0.8, stagger: 0.1, y: '0%', ease: 'power4.inOut' }, labelEnterAnim)
                .to(hotelTitleEnter, { duration: 0.6, className: 'anim-hotel-title shown' }, labelEnterAnim)
                .to(hotelRoomEnter, { duration: 1.2, className: 'anim-hotel-room shown' }, labelEnterAnim + '+=0.2');

            if (pointInfoEnter) {
                tl.to(pointInfoEnter, {
                    duration: 0.4,
                    height: pointInfoInnerEnter?.offsetHeight ?? 0,
                    ease: 'power2.inOut',
                    onComplete: function () {
                        if (pointInfoInnerEnter) {
                            pointInfoInnerEnter.style.height = '';
                        }
                    }
                }, labelEnterAnim + '+=0.4');
            }

            tlSlideTransition.value = tl;
        }, 0);
    }
};

const setProgress = (toIndex) => {
    let percent = 100 / props.items.length * toIndex + '%';
    if (twlProgressBar.value) {
        twlProgressBar.value.kill();
    }
    twlProgressBar.value = gsap.to('.progress-wrapper .progress-bar', { duration: 0.3, width: percent, height: percent, ease: 'power2.out' });
};

const hidePointInfo = (e) => {
    gsap.to((e.currentTarget).closest('.point-info'), { duration: 0.4, height: 0, ease: 'power2.inOut' });
};

watch(() => pageLoaded.value, (newState, oldState) => {
    if (!oldState && newState) {
        // Autoplay
        runProgress();
    }
});

useWheel(wheelHandler, {
    domTarget: slider
});

usePointerSwipe(slider, {
    onSwipeEnd(e, direction) {
        if (moreOneItem.value) {
            switch (direction) {
                case 'left':
                case 'up':
                    next();
                    break;
                case 'right':
                case 'down':
                    previous();
                    break;
            }
        }
    },
});

onMounted(() => {
    if (moreOneItem.value) {
        // Listen keyboard navigation
        document.addEventListener('keyup', function (e) {
            switch (e.code) {
                case 'ArrowUp':
                case 'PageUp':
                case 'ArrowLeft':
                    previous();
                    break;
                case 'Space':
                    if (e.ctrlKey) {
                        previous();
                    } else {
                        next();
                    }
                    break;
                case 'ArrowDown':
                case 'PageDown':
                case 'ArrowRight':
                    next();
                    break;
                default:
                    return;
            }
        });

        if (timerAutoplay.value === null && pageLoaded.value) {
            runProgress();
        }
    }
});

onBeforeUnmount(() => {
    clearTimeout(timerAutoplay.value);
    if (twlProgressBar.value) {
        twlProgressBar.value.kill();
    }
    if (tlSlideTransition.value) {
        tlSlideTransition.value.kill();
    }
});
</script>
